import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { ArrowLeft, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import { OverlayHistoryEffect } from "@/components/ui/overlayHistory"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[300] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/* ── Mobile detection hook ─────────────────────────────────────── */
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  )
  React.useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
    const handler = (e) => setIsMobile(e.matches)
    setIsMobile(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [breakpoint])
  return isMobile
}

/* ── Left-edge swipe-back gesture (mobile only) ────────────────── */
function useSwipeBack(ref, onClose, enabled) {
  React.useEffect(() => {
    if (!enabled) return
    const el = ref.current
    if (!el) return
    let startX = 0, startY = 0
    const onStart = (e) => { startX = e.touches[0].clientX; startY = e.touches[0].clientY }
    const onEnd = (e) => {
      const dx = e.changedTouches[0].clientX - startX
      const dy = Math.abs(e.changedTouches[0].clientY - startY)
      if (startX < 40 && dx > 72 && dy < 80) onClose()
    }
    el.addEventListener('touchstart', onStart, { passive: true, capture: true })
    el.addEventListener('touchend', onEnd, { passive: true, capture: true })
    return () => {
      el.removeEventListener('touchstart', onStart, { capture: true })
      el.removeEventListener('touchend', onEnd, { capture: true })
    }
  }, [ref, onClose, enabled])
}

/* ── Lock body scroll while a mobile fullscreen dialog is mounted ── */
function MobileScrollLock() {
  React.useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])
  return null
}

const DialogContent = React.forwardRef(({ className, children, hideCloseButton, compact, overlayClassName, ...props }, ref) => {
  const { t } = useTranslation()
  const closeButtonRef = React.useRef(null)
  const contentRef = React.useRef(null)
  const isMobile = useIsMobile()

  // Merge forwarded ref with our internal ref
  const mergedRef = React.useCallback((node) => {
    contentRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }, [ref])

  // Close handler for swipe-back
  const handleClose = React.useCallback(() => {
    closeButtonRef.current?.click()
  }, [])

  // Swipe-back gesture on mobile
  useSwipeBack(contentRef, handleClose, isMobile)

  /* ── MOBILE: full-screen overlay with back arrow (unless compact) ── */
  if (isMobile && !compact) {
    // Strip desktop-only classes that fight with full-screen layout
    const mobileClassName = (className || '')
      .replace(/max-w-\S+/g, '')
      .replace(/max-h-\S+/g, '')
      .replace(/translate-\S+/g, '')
      .replace(/left-\S+/g, '')
      .replace(/top-\S+/g, '')
      .replace(/rounded-\S+/g, '')
      .replace(/sm:rounded-\S+/g, '')
      .replace(/\bp-\d+\b/g, '')
      .replace(/\bsm:p-\d+\b/g, '')
      .replace(/\bgap-\d+\b/g, '')
      .replace(/\bborder\b/g, '')
      .replace(/\bshadow-\S+/g, '')
      .trim()

    return (
      <DialogPortal>
        <MobileScrollLock />
        <DialogPrimitive.Content
          ref={mergedRef}
          className={cn(
            "fixed inset-0 z-[300] flex flex-col bg-background text-foreground overflow-y-auto overflow-x-hidden px-4",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:slide-out-to-left-1/4 data-[state=open]:slide-in-from-left-1/4",
            mobileClassName
          )}
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          {...props}>
          <OverlayHistoryEffect closeFn={handleClose} />
          {children}
          {/* Hidden close button for programmatic close (back button, swipe) */}
          <DialogPrimitive.Close ref={closeButtonRef} className="hidden" />
        </DialogPrimitive.Content>
      </DialogPortal>
    )
  }

  /* ── DESKTOP: centered dialog (original behavior) ── */
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={mergedRef}
        className={cn(
          "fixed left-[50%] top-[50%] z-[300] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}>
        <OverlayHistoryEffect closeFn={handleClose} />
        {children}
        <DialogPrimitive.Close
          ref={closeButtonRef}
          className={cn(
            "absolute right-4 top-4 z-[100] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
            hideCloseButton && "hidden"
          )}>
          <X className="h-4 w-4" />
          <span className="sr-only">{t('common.close')}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

/* ── DialogHeader — on mobile: sticky back-arrow header ────────── */
const DialogHeader = ({
  className,
  children,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      // Mobile: sticky header bar with back arrow
      "max-sm:sticky max-sm:top-0 max-sm:z-10 max-sm:bg-background max-sm:border-b max-sm:border-border max-sm:flex-row max-sm:items-center max-sm:gap-2 max-sm:space-y-0 max-sm:-mx-4 max-sm:px-4 max-sm:text-left",
      className
    )}
    style={{ minHeight: typeof window !== 'undefined' && window.innerWidth < 640 ? 56 : undefined }}
    {...props}>
    {/* Back arrow — mobile only */}
    <DialogPrimitive.Close
      className="hidden max-sm:flex items-center justify-center min-h-[44px] min-w-[44px] p-3 rounded-full hover:bg-muted active:bg-muted transition-colors flex-shrink-0"
      aria-label="Go back">
      <ArrowLeft className="h-5 w-5" />
    </DialogPrimitive.Close>
    <div className="max-sm:flex-1 max-sm:min-w-0">
      {children}
    </div>
  </div>
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      // Mobile: sticky footer
      "max-sm:sticky max-sm:bottom-0 max-sm:bg-background max-sm:border-t max-sm:border-border max-sm:-mx-4 max-sm:p-4",
      className
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      "max-sm:text-base max-sm:truncate",
      className
    )}
    style={{ fontFamily: typeof window !== 'undefined' && window.innerWidth < 640 ? "'Barlow Condensed', sans-serif" : undefined }}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
